import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircleOutline,
  Restaurant,
  Dashboard,
  MenuBook,
  Settings,
  PhotoCamera,
} from '@mui/icons-material';
import POSRequestForm from '../../components/integration/POSRequestForm';

const ClaimSuccess = () => {
  const location = useLocation();
  const history = useHistory();
  const { venue, dashboardUrl } = location.state || {};
  const [secondsLeft, setSecondsLeft] = useState(40);

  useEffect(() => {
    document.title = 'Venue Claimed Successfully | Piatto';
  }, []);

  useEffect(() => {
    const countdown = setInterval(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);

    const redirect = setTimeout(() => {
      clearInterval(countdown);
      if (venue?.id) {
        history.push(`/editBusiness/${venue.id}`);
      }
    }, 40000);

    return () => {
      clearInterval(countdown);
      clearTimeout(redirect);
    };
  }, [dashboardUrl, venue, history]);

  const handleGoToDashboard = () => {
    if (venue?.id) {
      history.push(`/editBusiness/${venue.id}`);
    } else {
      history.push('/Home');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircleOutline
          sx={{ fontSize: 100, color: 'success.main', mb: 2 }}
          aria-hidden="true"
        />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Congratulations!{' '}
          <span aria-hidden="true">🎉</span>
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          You've successfully claimed your venue on Piatto
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {venue?.name || 'Your Venue'}
            </Typography>
            <Typography color="text.secondary">
              {venue?.address || ''}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            What's Next?
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MenuBook sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Manage Your Menu
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and update your imported menu, add new dishes, set prices
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Settings sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Configure Accessibility Features
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Showcase your venue's accessibility features (ramps, parking, bathrooms)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Restaurant sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Accept Pre-Arranged Orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start receiving orders from customers with accessibility requirements
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Dashboard sx={{ mr: 2, color: 'primary.main' }} aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Track Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor orders, revenue, and customer preferences
                </Typography>
              </Box>
            </Box>
          </Stack>

          {venue?.id && (
            <Box sx={{ mt: 3 }}>
              <POSRequestForm
                businessId={venue.id}
                business={{
                  bizName: venue.name || '',
                  bizAddress: venue.address || '',
                }}
              />
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            {venue?.id && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => history.push(`/editBusiness/${venue.id}/images`)}
                  startIcon={<PhotoCamera aria-hidden="true" />}
                  sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 600, bgcolor: '#B91C1C', '&:hover': { bgcolor: '#991B1B' } }}
                >
                  Set up your venue photos
                </Button>
              </Box>
            )}
            <Button
              variant="outlined"
              size="large"
              onClick={handleGoToDashboard}
              startIcon={<Dashboard aria-hidden="true" />}
              sx={{ py: 1.5, px: 6, fontSize: '1.1rem', fontWeight: 600 }}
            >
              Go to Venue Dashboard
            </Button>

            {/* aria-live so screen readers announce the countdown */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
              aria-live="polite"
              aria-atomic="true"
            >
              Redirecting automatically in {secondsLeft} second{secondsLeft !== 1 ? 's' : ''}...
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Need help getting started?{' '}
          <a
            href="https://piatto.com.au"
            style={{ color: '#B91C1C' }}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit piatto.com.au for help (opens in new tab)"
          >
            Visit piatto.com.au
          </a>
        </Typography>
      </Box>
    </Container>
  );
};

export default ClaimSuccess;
